/**
 * שחזור טיוטה (RQ-H06, שלב 5): טקסט שמוקלד נשמר במכשיר תוך כדי הקלדה — לא רק
 * כשיוצאים מהשדה. רענון, סגירה או טלפון שכבה באמצע משפט — הטקסט חוזר.
 *
 * - השמירה במכשיר: 300ms אחרי ההקלדה האחרונה, ומיד כשהאפליקציה יורדת לרקע
 *   (`visibilitychange` / `pagehide`).
 * - **סגירה או רענון מיד אחרי ההקלדה:** כתיבה ל־IndexedDB שמתחילה ב־`pagehide`
 *   נקטעת כשהדף נסגר (נמצא 29.09.2026: 10/10 טקסטים אבדו ברענון מיידי). לכן
 *   ב־`pagehide` / מעבר לרקע כל טיוטה שממתינה נכתבת **גם באופן מסונכרן** ל־
 *   localStorage (לכל חשבון בנפרד), ובפתיחה הבאה עוברת ל־IndexedDB. טיוטה נשארת
 *   ״ממתינה״ עד שהכתיבה ל־IndexedDB **הסתיימה**, לא רק התחילה.
 * - ״base״ — הערך שהיה מוצג כשהתחלתם לערוך. הוא עובר לפעולה בתור, וכך שינוי של
 *   חבר אחר באותו שדה בזמן שהקלדתם מזוהה כהתנגשות ולא נדרס (A-09).
 * - הערה (שדה שנשמר ביציאה ממנו) ששוחזרה — נשלחת לתור מיד: המשתמש הקליד אותה.
 *   טופס עם כפתור שליחה (ממצא, התראה) ששוחזר — חוזר לטופס ומסומן, ולא נשלח
 *   בלי לחיצה.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldDraft as DraftRecord } from './idbStore.ts';
import type { SyncHost } from './host.ts';

interface PendingDraft {
  userId: string;
  /** הטיוטה כפי שהיא עכשיו — מסונכרן, לכתיבת החירום. */
  record: () => DraftRecord;
  save: () => Promise<void>;
}

const pending = new Map<string, PendingDraft>();
let listening = false;

const RESCUE_PREFIX = 'hakshuri-draft-rescue:';

function readRescue(userId: string): Record<string, DraftRecord> {
  try {
    return JSON.parse(localStorage.getItem(RESCUE_PREFIX + userId) ?? '{}') as Record<
      string,
      DraftRecord
    >;
  } catch {
    return {};
  }
}

function writeRescue(userId: string, drafts: Record<string, DraftRecord>): void {
  try {
    if (Object.keys(drafts).length === 0) localStorage.removeItem(RESCUE_PREFIX + userId);
    else localStorage.setItem(RESCUE_PREFIX + userId, JSON.stringify(drafts));
  } catch {
    // אחסון מלא / חסום — נשארת הכתיבה ל־IndexedDB
  }
}

function dropRescue(userId: string, key: string): void {
  const drafts = readRescue(userId);
  if (key in drafts) {
    delete drafts[key];
    writeRescue(userId, drafts);
  }
}

/** D35: טיוטות חירום של ביקורים באתר שנמחק לצמיתות — נמחקות (לא יעברו ל־IndexedDB). */
export function dropRescuedDrafts(userId: string, visitIds: readonly string[]): void {
  const ids = new Set(visitIds);
  const drafts = readRescue(userId);
  const kept = Object.fromEntries(Object.entries(drafts).filter(([, d]) => !ids.has(d.visitId)));
  if (Object.keys(kept).length !== Object.keys(drafts).length) writeRescue(userId, kept);
}

/** כתיבה מסונכרנת של כל טיוטה שממתינה — שורדת סגירה מיידית של הדף. */
export function rescueDrafts(): void {
  const byUser = new Map<string, Record<string, DraftRecord>>();
  for (const [key, entry] of pending) {
    const drafts = byUser.get(entry.userId) ?? readRescue(entry.userId);
    drafts[key] = entry.record();
    byUser.set(entry.userId, drafts);
  }
  for (const [userId, drafts] of byUser) writeRescue(userId, drafts);
}

const moved = new WeakMap<SyncHost, Promise<void>>();

/**
 * טיוטות שנכתבו בחירום (בסגירת הדף) — עוברות ל־IndexedDB, אם הן חדשות ממה שיש שם.
 * פעם אחת לכל חשבון; כל טעינת טיוטה ממתינה לזה.
 */
export function restoreRescuedDrafts(host: SyncHost): Promise<void> {
  let done = moved.get(host);
  if (done === undefined) {
    done = (async () => {
      const drafts = readRescue(host.userId);
      for (const draft of Object.values(drafts)) {
        const existing = await host.store.getDraft(draft.key).catch(() => undefined);
        if (existing === undefined || existing.updatedAt <= draft.updatedAt)
          await host.store.putDraft(draft);
      }
      writeRescue(host.userId, {});
    })().catch(() => undefined);
    moved.set(host, done);
  }
  return done;
}

/** שומר עכשיו כל טקסט שממתין לשמירה. */
export async function flushDrafts(): Promise<void> {
  const tasks = [...pending.values()];
  await Promise.all(tasks.map((t) => t.save().catch(() => undefined)));
}

function listen(): void {
  if (listening) return;
  listening = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      rescueDrafts();
      void flushDrafts();
    }
  });
  window.addEventListener('pagehide', () => {
    rescueDrafts();
    void flushDrafts();
  });
}

function equal(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export interface FieldDraft<T> {
  value: T;
  set: (value: T) => void;
  /** שולח לתור (אם השתנה) ומוחק את הטיוטה. */
  commit: () => Promise<void>;
  /** מבטל את הטיוטה וחוזר לערך המוצג. */
  reset: () => Promise<void>;
  restored: boolean;
  dirty: boolean;
  error: string | null;
}

export function useFieldDraft<T>(
  host: SyncHost,
  visitId: string,
  key: string,
  shown: T,
  onCommit: (value: T, base: T) => Promise<void>,
  options: { autoCommitRestored?: boolean } = {},
): FieldDraft<T> {
  listen();
  const [value, setValue] = useState<T>(shown);
  const [restored, setRestored] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = useRef<T>(shown);
  const latest = useRef({ value, dirty, shown, onCommit });
  latest.current = { value, dirty, shown, onCommit };
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // כל הקלדה מעלה את המונה; טיוטה יוצאת מ״ממתינות״ רק כשנשמרה הגרסה האחרונה שלה.
  const version = useRef(0);
  const record = useCallback(
    (): DraftRecord => ({
      key,
      visitId,
      value: latest.current.value,
      base: base.current,
      updatedAt: new Date().toISOString(),
    }),
    [key, visitId],
  );
  const save = useCallback(async () => {
    const v = version.current;
    await host.store.putDraft(record());
    if (version.current === v) {
      pending.delete(key);
      dropRescue(host.userId, key);
    }
  }, [host, key, record]);

  const commit = useCallback(async () => {
    if (timer.current !== null) clearTimeout(timer.current);
    pending.delete(key);
    dropRescue(host.userId, key);
    const { value: v, dirty: d, onCommit: send } = latest.current;
    if (!d) return;
    const sent = version.current;
    try {
      if (!equal(v, base.current)) await send(v, base.current);
      if (version.current === sent) await host.store.deleteDraft(key);
      // הבדיקה אחרי **כל** ההמתנות — גם מחיקת הטיוטה היא await שבמהלכו אפשר להקליד.
      if (version.current !== sent) {
        // הוקלד עוד תוך כדי השליחה (למשל בחירה ואז הקלדה מיד אחריה): השדה נשאר ״בעריכה״,
        // ומה שנשלח הוא הבסיס לשליחה הבאה — אחרת הטקסט החדש היה נמחק ב״חזרה לערך המוצג״
        // (נמצא 01.10.2026 בבדיקת D22: סוג ״אחר״ ואז הטקסט שלו).
        base.current = v;
        setError(null);
        return;
      }
      setDirty(false);
      setError(null);
    } catch (e) {
      // האחסון מלא או לא זמין — הטקסט נשאר בשדה, והמשתמש יודע (RQ-H07).
      setError(`לא נשמר במכשיר: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [host, key]);

  // טעינת טיוטה שנשארה מהפעם הקודמת.
  useEffect(() => {
    let cancelled = false;
    void restoreRescuedDrafts(host)
      .then(() => host.store.getDraft(key))
      .then((draft) => {
        if (cancelled || draft === undefined) return;
        base.current = draft.base as T;
        setValue(draft.value as T);
        setDirty(true);
        setRestored(true);
        latest.current = { ...latest.current, value: draft.value as T, dirty: true };
        if (options.autoCommitRestored) void commit();
      });
    return () => {
      cancelled = true;
    };
  }, [host, key]);

  // כל עוד לא עורכים — השדה מציג את הערך העדכני (כולל שינוי של חבר אחר).
  useEffect(() => {
    if (!latest.current.dirty) {
      setValue(shown);
      base.current = shown;
    }
  }, [JSON.stringify(shown)]);

  const set = useCallback(
    (next: T) => {
      if (!latest.current.dirty) base.current = latest.current.shown;
      setRestored(false);
      setValue(next);
      setDirty(true);
      latest.current = { ...latest.current, value: next, dirty: true };
      version.current += 1;
      pending.set(key, { userId: host.userId, record, save });
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => void save().catch((e) => setError(String(e))), 300);
    },
    [host, key, record, save],
  );

  const reset = useCallback(async () => {
    if (timer.current !== null) clearTimeout(timer.current);
    pending.delete(key);
    dropRescue(host.userId, key);
    await host.store.deleteDraft(key);
    setDirty(false);
    setRestored(false);
    setValue(latest.current.shown);
    base.current = latest.current.shown;
  }, [host, key]);

  return { value, set, commit, reset, restored, dirty, error };
}
